import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectMyWalletReimbursementIdFromRoute,
  selectSubmittingExpenseReportErrorFromState,
  selectSubmittingExpenseReportFromState,
} from 'src/app/ExpenseReport/redux/expenseReport.selectors'
import { ButtonDesigns } from 'src/components/Button/Button'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import { useTranslate } from 'src/localization/Trns'
import { createSetIsSubmitExpenseReportPopupOpen } from 'src/redux/actions/app.actions'
import { postSubmitExpenseReport } from 'src/redux/actions/data/expenses.actions'
import { numberOfExpensesFromState } from 'src/redux/selectors/data/expenses.selectors'
import { css } from 'styled-components'

const SubmitExpenseReportPopup: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()
  const submittingExpenseReport = useSelector(
    selectSubmittingExpenseReportFromState,
  )
  const expenseReportId = useSelector(selectMyWalletReimbursementIdFromRoute)
  const topLevelError = useSelector(selectSubmittingExpenseReportErrorFromState)
  const numberOfExpenses = useSelector(numberOfExpensesFromState)
  const { analytics } = useAnalyticsContext()

  const handleReportSubmit = (): void => {
    if (numberOfExpenses === undefined) return
    dispatch(
      postSubmitExpenseReport(expenseReportId, numberOfExpenses, analytics),
    )
  }

  const closePopup = (): void => {
    dispatch(createSetIsSubmitExpenseReportPopupOpen(false))
  }

  const subTitleTopText = translate(
    'Once you submit, you ${boldedText} be able to make any changes to the expense name, amount, or date of purchase of your expenses. You’ll still be able to upload receipts and make notes.',
    {
      boldedText: (
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          {translate('will not')}
        </span>
      ),
    },
  )

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={translate('Submit Report')}
        subtitle={
          <>
            <p
              css={css`
                margin-bottom: 20px;
              `}
            >
              {subTitleTopText}
            </p>
            <p
              css={css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate(
                'Please make sure your report is finalized before submitting.',
              )}
            </p>
          </>
        }
        actionButtonLabel={translate('Confirm and Submit')}
        actionButtonOnClick={handleReportSubmit}
        actionButtonLoading={submittingExpenseReport}
        actionButtonDesign={ButtonDesigns.designCta}
        topLevelError={topLevelError}
        isCancelButtonLink
        underline={false}
        cancelButtonOnClick={closePopup}
        cancelButtonLabel={translate('Cancel')}
      />
    </Popup>
  )
}

export default SubmitExpenseReportPopup
